import { useEffect } from 'react';
const DEFAULT_TITLE = 'Thruline';
function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

/**
 * Per-route document title and description.
 *
 * Phase A only — this mutates document.head in an effect, which is enough for
 * browser tabs, bookmarks, and link previews that execute JS. It is NOT enough
 * for crawlers: a prerendered shell would still carry index.html's generic
 * title. Phase B must feed the same guide registry into the prerender output
 * and assert the generated HTML contains the title, description, canonical,
 * and JSON-LD. See section 15.6 of design/reference-guides-plan.md.
 */
export function useDocumentMeta({
  title,
  description,
  noindex = false
}: {
  title: string;
  description?: string;
  /** Keeps error and not-found states out of the index. */
  noindex?: boolean;
}) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    setMeta('property', 'og:title', title);
    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
    }
    return () => {
      document.title = previous || DEFAULT_TITLE;
    };
  }, [title, description]);

  // React 18 does not hoist <meta> out of the component tree, so rendering one
  // in JSX leaves it in the body where it means nothing. Append to head here
  // and remove on unmount, or the whole site inherits noindex after one visit
  // to a missing page.
  useEffect(() => {
    if (!noindex) return;
    const el = document.createElement('meta');
    el.name = 'robots';
    el.content = 'noindex';
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, [noindex]);
}